/**
 * Server-relative time (CONVENTIONS rule 12). The launch and refresh responses carry
 * `server_time`; the offset is applied to every expiry and deadline computation so a wrong
 * local clock never shortens or extends a session, a ring or a rejoin window.
 */
export interface Clock {
  /** server-relative epoch milliseconds */
  now(): number;
}

export class ServerClock implements Clock {
  private offsetMs = 0;

  constructor(private readonly local: () => number = () => Date.now()) {}

  /** `serverTimeIso` was observed when the local clock read `localAtResponse` (defaults to now). */
  sync(serverTimeIso: string, localAtResponse: number = this.local()): void {
    const t = Date.parse(serverTimeIso);
    if (Number.isNaN(t)) return;
    this.offsetMs = t - localAtResponse;
  }

  now(): number {
    return this.local() + this.offsetMs;
  }

  /** server minus local, milliseconds */
  get offset(): number {
    return this.offsetMs;
  }

  /** absolute skew in seconds; > 120 is the amber self-check item */
  skewSec(): number {
    return Math.abs(this.offsetMs) / 1000;
  }

  /** milliseconds until an ISO wall-clock deadline, never negative */
  until(iso: string): number {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return 0;
    return Math.max(0, t - this.now());
  }
}

export const CLOCK_SKEW_WARN_SEC = 120;
